import React, { useState } from 'react';
import { CheckCircle2, AlertCircle, Database, Layers, GitBranch, Key, Sparkles } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Button from '../../components/ui/Button';
import Card, { CardBody, CardHeader } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import Spinner from '../../components/ui/Spinner';
import { useAnalysisStore } from '../../stores/analysisStore';
import { api } from '../../lib/api';
import { completeSchemaDesign } from '../../lib/api/knowledgeGraphDesignApi';
import { extractErrorMessage } from '../../lib/apiErrors';
import { clearDraft } from '../../lib/analysisWizardDraft';

interface Step5Props {
  caseData: any;
  onBack: () => void;
}

export const Step5ReviewComplete: React.FC<Step5Props> = ({ caseData, onBack }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const caseId = searchParams.get('caseId');
  const { enterpriseSystems, customSystems, dataMappings, schemaDesign, schemaPreviews } =
    useAnalysisStore();

  /**
   * Assemble the `schema_data` payload for POST /schema/complete.
   *
   * Preferred source is the raw server previews from Step 4 — they carry
   * mermaid_diagram, coverage_analysis and gds_recommendations, which the
   * UI-facing `schemaDesign` projection discards. When several systems were
   * previewed their tables/relationships are concatenated.
   *
   * Falls back to deriving tables from `schemaDesign` for cases whose schema
   * came from a path that produced no preview (e.g. the streaming generator),
   * so completing is still possible — just without coverage/GDS extras.
   *
   * Returns null when there is genuinely nothing to send, so the caller can
   * fail loudly instead of POSTing an empty object.
   */
  const buildSchemaData = (): Record<string, any> | null => {
    if (schemaPreviews && schemaPreviews.length > 0) {
      const merged = schemaPreviews.reduce(
        (acc: Record<string, any>, p: any) => ({
          case_id: p.case_id ?? acc.case_id ?? caseId,
          system_id: acc.system_id ?? p.system_id,
          tables: [...(acc.tables ?? []), ...(p.tables ?? [])],
          relationships: [...(acc.relationships ?? []), ...(p.relationships ?? [])],
          // Keep the first non-empty diagram/DDL rather than concatenating
          // two documents into something that renders as neither.
          mermaid_diagram: acc.mermaid_diagram || p.mermaid_diagram || '',
          cypher_ddl: acc.cypher_ddl || p.cypher_ddl || '',
          coverage_analysis: acc.coverage_analysis ?? p.coverage_analysis,
          gds_recommendations: [
            ...(acc.gds_recommendations ?? []),
            ...(p.gds_recommendations ?? []),
          ],
        }),
        {} as Record<string, any>,
      );

      if ((merged.tables ?? []).length > 0) {
        if (!merged.cypher_ddl && schemaDesign?.cypherDDL) {
          merged.cypher_ddl = schemaDesign.cypherDDL;
        }
        return merged;
      }
    }

    // Fallback: project the store's schemaDesign into the request shape.
    if (schemaDesign && schemaDesign.nodes.length > 0) {
      return {
        case_id: caseId,
        tables: schemaDesign.nodes.map((n) => ({
          table_name: n.label,
          node_label: n.label,
          columns: (n.properties ?? []).map((p) => ({
            column_name: p.name,
            data_type: p.type,
            is_primary_key: !!p.unique,
          })),
        })),
        relationships: schemaDesign.relationships.map((r) => ({
          relationship_type: r.type,
          source_table: r.from,
          target_table: r.to,
        })),
        mermaid_diagram: '',
        cypher_ddl: schemaDesign.cypherDDL || '',
        coverage_analysis: {},
        gds_recommendations: [],
      };
    }

    return null;
  };

  const [validationChecks, setValidationChecks] = useState({
    systemsSelected: false,
    mappingsApproved: false,
    schemaGenerated: false,
  });

  // Calculate summary statistics
  const selectedSystems = enterpriseSystems.filter(s => s.selected);
  const totalSystems = selectedSystems.length + customSystems.length;
  const approvedMappings = dataMappings.filter(m => m.approved);
  // Canvas-based cases produce no DataMapping records: the checklist reports
  // approval as "not applicable", and the summary hides the mapping tiles/card
  // instead of showing "0 Data Mappings / 0%".
  const hasDataMappings = dataMappings.length > 0;
  const approvalRate = dataMappings.length > 0
    ? Math.round((approvedMappings.length / dataMappings.length) * 100)
    : 0;

  // Validation checks
  React.useEffect(() => {
    setValidationChecks({
      systemsSelected: totalSystems > 0,
      // With zero mappings this read `0 >= Math.ceil(0)` → true, so the gate
      // showed a green tick for "(0/0 approved)". The canvas-only flow can
      // legitimately produce no DataMapping nodes, so treat "none exist" as
      // not-applicable rather than as a pass with a misleading label.
      mappingsApproved:
        dataMappings.length === 0
          ? true
          : approvedMappings.length >= Math.ceil(dataMappings.length * 0.5),
      // `schemaDesign !== null` passed for an empty schema object. Require
      // actual nodes — this is the check that let a case reach Design with
      // nothing to design from.
      schemaGenerated: !!schemaDesign && schemaDesign.nodes.length > 0,
    });
  }, [totalSystems, approvedMappings.length, dataMappings.length, schemaDesign]);

  const allChecksPass = Object.values(validationChecks).every(check => check);

  // Mutation to complete Analysis phase
  const completeAnalysisMutation = useMutation({
    mutationFn: async () => {
      if (!caseId) throw new Error('No case ID');

      // STEP 1 — persist the schema as a CompletedSchema node FIRST.
      //
      // This step previously only PATCHed the case to currentPhase: 'Design'.
      // The phase advanced, the checklist ticked "Neo4j schema design
      // generated" (reading client state, not the database), and the case
      // arrived in Design with no HAS_COMPLETED_SCHEMA relationship and a
      // CompletedSchema count of 0 — so Design had nothing to work from.
      //
      // `schema_data` must be populated: an empty object yields an empty
      // CompletedSchema, which is the same dead end. We send the raw server
      // previews captured in Step 4 rather than the lossy `schemaDesign`
      // projection, which drops mermaid_diagram, coverage_analysis and
      // gds_recommendations.
      const schemaData = buildSchemaData();
      if (!schemaData) {
        throw new Error(
          'No generated schema found for this case. Go back to Schema Design and generate the schema before completing the Analysis phase.',
        );
      }

      const completion = await completeSchemaDesign(caseId, schemaData as any);

      // The endpoint answers 200 even when it stored nothing useful, so check
      // the counts rather than trusting the status code alone.
      if (completion && completion.success === false) {
        throw new Error(completion.message || 'Schema completion was rejected by the server.');
      }
      if (completion && completion.entities_count === 0) {
        throw new Error(
          'The server saved an empty schema (0 entities). Regenerate the schema in Schema Design and try again.',
        );
      }

      // STEP 2 — only now advance the phase.
      const analysisData = {
        enterpriseSystems: selectedSystems,
        customSystems,
        dataMappings: approvedMappings,
        schemaDesign,
        completedAt: new Date().toISOString(),
      };

      // analysisPhaseStatus also moves to "Completed" so the CaseLibrary's
      // phase badge and routing both reflect that Analysis is done — without
      // it the case would route to Design correctly but still display
      // "Analysis: In Progress" on the dashboard.
      const updatedCase = await api.patch<Record<string, unknown>>(`/cases/${caseId}`, {
        analysisData,
        currentPhase: 'Design',
        analysisPhaseStatus: 'Completed',
      });

      // Write the transition into the shared ['case', caseId] cache entry now.
      // DesignWizard's phase guard and AnalysisWizard's landing rule both read
      // this key; left alone it still says currentPhase: 'Analysis' (staleTime
      // 5 min) and they bounced the user back to Step 0 with the draft gone.
      queryClient.setQueryData(['case', caseId], (prev: Record<string, unknown> | undefined) => ({
        ...(prev ?? {}),
        ...(updatedCase ?? {}),
        currentPhase: 'Design',
        analysisPhaseStatus: 'Completed',
      }));

      return { caseId, completion };
    },
    onSuccess: async ({ caseId }) => {
      // Refetch the case BEFORE clearing the draft and navigating so every
      // consumer of ['case', caseId] sees the server's currentPhase: 'Design'.
      await queryClient.invalidateQueries({ queryKey: ['case', caseId] });
      // Phase has transitioned to Design — the saved "where I left off in
      // Analysis" pointer is now stale.  Clear it so revisiting the Analysis
      // wizard for this case lands at Step 0 instead of resuming the
      // already-completed Step 5.
      clearDraft(caseId);
      navigate(`/cases/design-wizard?caseId=${caseId}`);
    },
    onError: (error) => {
      console.error('Failed to complete Analysis phase:', error);
    },
  });

  const handleComplete = () => {
    if (!allChecksPass) return;
    completeAnalysisMutation.mutate();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-neutral-900 mb-2">
          Review & Complete Analysis Phase
        </h2>
        <p className="text-neutral-600">
          Verify all requirements before proceeding to Design phase
        </p>
      </div>

      {/* Validation Checklist */}
      <Card className={allChecksPass ? 'bg-gradient-to-r from-green-50 to-emerald-50' : 'bg-gradient-to-r from-amber-50 to-orange-50'}>
        <CardBody>
          <div className="flex items-center gap-3 mb-4">
            {allChecksPass ? (
              <CheckCircle2 className="w-6 h-6 text-green-600" />
            ) : (
              <AlertCircle className="w-6 h-6 text-amber-600" />
            )}
            <h3 className="font-semibold text-neutral-900">
              {allChecksPass ? 'Ready to Complete' : 'Validation Incomplete'}
            </h3>
          </div>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              {validationChecks.systemsSelected ? (
                <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
              ) : (
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
              )}
              <span className={validationChecks.systemsSelected ? 'text-neutral-700' : 'text-amber-900 font-medium'}>
                At least one enterprise system selected ({totalSystems} selected)
              </span>
            </div>
            <div className="flex items-center gap-3">
              {validationChecks.mappingsApproved ? (
                <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
              ) : (
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
              )}
              <span className={validationChecks.mappingsApproved ? 'text-neutral-700' : 'text-amber-900 font-medium'}>
                {dataMappings.length === 0
                  ? 'Data mapping approval — not applicable (canvas-based mapping)'
                  : `At least 50% of data mappings approved (${approvedMappings.length}/${dataMappings.length} approved)`}
              </span>
            </div>
            <div className="flex items-center gap-3">
              {validationChecks.schemaGenerated ? (
                <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
              ) : (
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
              )}
              <span className={validationChecks.schemaGenerated ? 'text-neutral-700' : 'text-amber-900 font-medium'}>
                Neo4j schema design generated
                {schemaDesign ? ` (${schemaDesign.nodes.length} nodes)` : ''}
              </span>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Summary Statistics */}
      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold text-neutral-900">Analysis Phase Summary</h3>
        </CardHeader>
        <CardBody>
          <div className={hasDataMappings ? 'grid grid-cols-2 md:grid-cols-4 gap-6' : 'grid grid-cols-2 gap-6'}>
            <div className="text-center">
              <div className="flex justify-center mb-2">
                <Database className="w-8 h-8 text-amber-600" />
              </div>
              <p className="text-3xl font-bold text-neutral-900">{totalSystems}</p>
              <p className="text-sm text-neutral-600">Enterprise Systems</p>
            </div>
            {hasDataMappings && (
              <>
                <div className="text-center">
                  <div className="flex justify-center mb-2">
                    <Layers className="w-8 h-8 text-blue-600" />
                  </div>
                  <p className="text-3xl font-bold text-neutral-900">{dataMappings.length}</p>
                  <p className="text-sm text-neutral-600">Data Mappings</p>
                </div>
                <div className="text-center">
                  <div className="flex justify-center mb-2">
                    <CheckCircle2 className="w-8 h-8 text-green-600" />
                  </div>
                  <p className="text-3xl font-bold text-neutral-900">{approvalRate}%</p>
                  <p className="text-sm text-neutral-600">Approval Rate</p>
                </div>
              </>
            )}
            <div className="text-center">
              <div className="flex justify-center mb-2">
                <GitBranch className="w-8 h-8 text-purple-600" />
              </div>
              <p className="text-3xl font-bold text-neutral-900">{schemaDesign?.nodes.length || 0}</p>
              <p className="text-sm text-neutral-600">Schema Nodes</p>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Enterprise Systems Summary */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Database className="w-5 h-5 text-amber-600" />
            <h3 className="font-semibold text-neutral-900">Selected Enterprise Systems</h3>
            <Badge variant="secondary">{totalSystems}</Badge>
          </div>
        </CardHeader>
        <CardBody>
          <div className="flex flex-wrap gap-2">
            {selectedSystems.map(system => (
              <div key={system.systemId} className="px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm font-medium text-neutral-900">{system.name}</p>
                <p className="text-xs text-neutral-600">{system.vendor} • {system.type}</p>
              </div>
            ))}
            {customSystems.map((system, index) => (
              <div key={index} className="px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm font-medium text-neutral-900">{system}</p>
                <p className="text-xs text-neutral-600">Custom System</p>
              </div>
            ))}
            {totalSystems === 0 && (
              <p className="text-sm text-neutral-500 italic">No systems selected</p>
            )}
          </div>
        </CardBody>
      </Card>

      {/* Data Mappings Summary - omitted for canvas-based cases (no DataMapping records) */}
      {hasDataMappings && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Layers className="w-5 h-5 text-blue-600" />
              <h3 className="font-semibold text-neutral-900">Data Mappings</h3>
              <Badge variant="secondary">{dataMappings.length} total</Badge>
              <Badge variant="success">{approvedMappings.length} approved</Badge>
            </div>
          </CardHeader>
          <CardBody>
            {dataMappings.length > 0 ? (
              <div className="space-y-2">
                {approvedMappings.slice(0, 5).map((mapping, index) => (
                  <div key={mapping.mappingId} className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-start justify-between mb-1">
                      <p className="text-sm font-medium text-neutral-900 flex-1">
                        {mapping.questionText}
                      </p>
                      <Badge variant="success" className="ml-2">
                        {(mapping.confidence * 100).toFixed(0)}%
                      </Badge>
                    </div>
                    <p className="text-xs text-neutral-600">
                      {mapping.sourceSystem} • {mapping.sourceTable} → {mapping.targetEntity}
                    </p>
                  </div>
                ))}
                {approvedMappings.length > 5 && (
                  <p className="text-sm text-neutral-500 text-center pt-2">
                    + {approvedMappings.length - 5} more approved mappings
                  </p>
                )}
              </div>
            ) : (
              <p className="text-sm text-neutral-500 italic text-center py-4">No data mappings created</p>
            )}
          </CardBody>
        </Card>
      )}

      {/* Schema Design Summary */}
      {schemaDesign && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <GitBranch className="w-5 h-5 text-purple-600" />
              <h3 className="font-semibold text-neutral-900">Neo4j Schema Design</h3>
            </div>
          </CardHeader>
          <CardBody>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-2xl font-bold text-blue-700">{schemaDesign.nodes.length}</p>
                <p className="text-xs text-neutral-600">Node Types</p>
              </div>
              <div className="p-3 bg-purple-50 rounded-lg">
                <p className="text-2xl font-bold text-purple-700">{schemaDesign.relationships.length}</p>
                <p className="text-xs text-neutral-600">Relationships</p>
              </div>
              <div className="p-3 bg-green-50 rounded-lg">
                <p className="text-2xl font-bold text-green-700">{schemaDesign.constraints.length}</p>
                <p className="text-xs text-neutral-600">Constraints</p>
              </div>
              <div className="p-3 bg-amber-50 rounded-lg">
                <p className="text-2xl font-bold text-amber-700">{schemaDesign.indexes.length}</p>
                <p className="text-xs text-neutral-600">Indexes</p>
              </div>
            </div>

            <div>
              <p className="text-xs font-medium text-neutral-600 mb-2">Node Types:</p>
              <div className="flex flex-wrap gap-2">
                {schemaDesign.nodes.map((node, index) => (
                  <span key={index} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-mono">
                    :{node.label}
                  </span>
                ))}
              </div>
            </div>

            {schemaDesign.relationships.length > 0 && (
              <div className="mt-3">
                <p className="text-xs font-medium text-neutral-600 mb-2">Relationships:</p>
                <div className="flex flex-wrap gap-2">
                  {schemaDesign.relationships.map((rel, index) => (
                    <span key={index} className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-mono">
                      [{rel.type}]
                    </span>
                  ))}
                </div>
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {/* Next Steps Preview */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex gap-3">
          <Sparkles className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900">
            <p className="font-medium mb-1">What's Next: Design Phase</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Generate detailed Neo4j schema implementation code</li>
              <li>Create data loader scripts for enterprise systems</li>
              <li>Design executive analytics queries and dashboards</li>
              <li>Prepare deployment documentation and configuration</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-6">
        <Button variant="outline" onClick={onBack}>
          Back to Schema Preview
        </Button>
        <Button
          onClick={handleComplete}
          disabled={!allChecksPass || completeAnalysisMutation.isPending}
        >
          {completeAnalysisMutation.isPending ? (
            <>
              <Spinner size="sm" className="mr-2" />
              Completing...
            </>
          ) : (
            <>
              <CheckCircle2 className="w-4 h-4 mr-2" />
              {allChecksPass ? 'Complete Analysis Phase' : 'Cannot Proceed - Check Requirements'}
            </>
          )}
        </Button>
      </div>

      {completeAnalysisMutation.isError && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <div className="text-sm text-red-900">
              <p className="font-medium mb-1">Error Completing Analysis Phase</p>
              {/* Show what the server actually said. The backend returns
                  actionable 400/404/422 detail naming exactly what is wrong;
                  replacing it with "please try again" threw that away. */}
              <p className="whitespace-pre-wrap">
                {extractErrorMessage(completeAnalysisMutation.error)}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Step5ReviewComplete;
